/*
 * Copyright (c) 2026. Tooling Team
 */

import { Button } from "@uhg-abyss/web/ui/Button";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import { downloadCsv } from "@uhg-abyss/web/tools/downloadCsv";
import moment from "moment";
import PropTypes from "prop-types";
import { useEffect } from "react";

const ABYSS_COLUMN_IDS = [
  "abyss-edit-action",
  "abyss-reorder-row",
  "abyss-action",
  "abyss-select",
  "abyss-expand",
];

const DEFAULT_DOWNLOAD_FILENAMES = {
  all: "AllData.csv",
  currentPage: "CurrentPageData.csv",
  filtered: "FilteredData.csv",
};
//update these if the Abyss filter selector updates their class names or structure
//Workaround to skip having to click to add the first filter
const ADVANCED_FILTER_ROOT_SELECTOR = ".abyss-data-table-advanced-filter-root";
const ADD_FILTER_BUTTON_SELECTOR =
  ".abyss-data-table-advanced-filter-add-filter-button";
const FILTER_ROW_SELECTOR = ".abyss-data-table-advanced-filter-filter-row";
const SEEDED_FILTER_ATTRIBUTE = "data-mvd-default-filter-seeded";

// Abyss date filters compare date-only values; cells still render original timestamps.
const DATE_FILTER_FORMATS = [
  moment.ISO_8601,
  "MM-DD-YYYY",
  "MM-DD-YYYY HH:mm:ss",
  "MM-DD-YYYY  HH:mm:ss",
  "MM/DD/YYYY",
  "MM/DD/YYYY HH:mm:ss",
  "YYYY-MM-DD",
  "YYYY-MM-DD HH:mm:ss",
];

const parseNumericDateValue = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return null;
  }

  const absoluteValue = Math.abs(numericValue);
  return absoluteValue > 0 && absoluteValue < 100000000000
    ? moment.unix(numericValue)
    : moment(numericValue);
};

export const parseDateFilterValue = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  if (
    typeof value === "number" ||
    (typeof value === "string" && /^-?\d+$/.test(value.trim()))
  ) {
    const numericDate = parseNumericDateValue(value);
    return numericDate?.isValid() ? numericDate : null;
  }

  const parsedDate =
    typeof value === "string"
      ? moment(value.trim(), DATE_FILTER_FORMATS, true)
      : moment(value, DATE_FILTER_FORMATS, true);

  if (parsedDate.isValid()) {
    return parsedDate;
  }

  if (typeof value === "string") {
    return null;
  }

  const fallbackDate = moment(value);
  return fallbackDate.isValid() ? fallbackDate : null;
};

export const formatDateOnlyFilterValue = (value) => {
  const parsedDate = parseDateFilterValue(value);
  return parsedDate ? parsedDate.format("MM/DD/YYYY") : "";
};

//util function for sorting date/time columns
//this is because we need to use the date without time for filter
//but with time for sort
export const compareDateColumnValues = (rowA, rowB, columnKey) => {
  const leftDate = parseDateFilterValue(rowA.original?.[columnKey]);
  const rightDate = parseDateFilterValue(rowB.original?.[columnKey]);
  const leftValue = leftDate ? leftDate.valueOf() : 0;
  const rightValue = rightDate ? rightDate.valueOf() : 0;

  return leftValue === rightValue ? 0 : leftValue > rightValue ? 1 : -1;
};

export const seedDefaultAdvancedFilterInput = (root) => {
  if (!root) {
    return;
  }

  root.querySelectorAll(ADVANCED_FILTER_ROOT_SELECTOR).forEach((filterRoot) => {
    if (filterRoot.getAttribute(SEEDED_FILTER_ATTRIBUTE) === "true") {
      return;
    }

    const addFilterButton = filterRoot.querySelector(
      ADD_FILTER_BUTTON_SELECTOR
    );
    if (!addFilterButton || filterRoot.querySelector(FILTER_ROW_SELECTOR)) {
      return;
    }

    filterRoot.setAttribute(SEEDED_FILTER_ATTRIBUTE, "true");
    addFilterButton.click();
  });
};

export const useDefaultAdvancedFilterInput = () => {
  useEffect(() => {
    if (
      typeof document === "undefined" ||
      typeof MutationObserver === "undefined"
    ) {
      return undefined;
    }

    const observer = new MutationObserver(() => {
      seedDefaultAdvancedFilterInput(document.body);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    seedDefaultAdvancedFilterInput(document.body);

    return () => {
      observer.disconnect();
    };
  }, []);
};

export const getCsvHeader = (column) =>
  column.columnDef?.meta?.headerLabel ||
  (typeof column.columnDef?.header === "string"
    ? column.columnDef.header
    : column.id);

export const getCsvCellValue = (row, columnId) => {
  const cell = row
    .getAllCells()
    .find((rowCell) => rowCell.column.id === columnId);
  const customSetCellCsv = cell?.column?.columnDef?.meta?.customSetCellCsv;

  if (customSetCellCsv) {
    return customSetCellCsv({
      value: cell.getValue(),
      cell,
      row,
      original: row?.original,
    });
  }

  return cell ? cell.getValue() : "";
};

export const getDownloadColumns = (tableInstance, additionalColumnMap = {}) => {
  const visibleColumns = tableInstance
    .getVisibleLeafColumns()
    .filter((column) => !ABYSS_COLUMN_IDS.includes(column.id));

  return visibleColumns.flatMap((column) => {
    const csvColumn = {
      id: column.id,
      header: getCsvHeader(column),
    };
    const additionalColumn = additionalColumnMap[column.id];

    return additionalColumn ? [csvColumn, additionalColumn] : [csvColumn];
  });
};

export const downloadRowsAsCsv = ({
  tableInstance,
  rows,
  filename,
  additionalColumnMap,
  getAdditionalColumnValue,
}) => {
  const csvColumns = getDownloadColumns(tableInstance, additionalColumnMap);
  const additionalColumnIds = new Set(
    Object.values(additionalColumnMap || {}).map((column) => column.id)
  );
  const data = rows.map((row) =>
    csvColumns.reduce(
      (rowData, column) => ({
        ...rowData,
        [column.id]:
          typeof getAdditionalColumnValue === "function" &&
          additionalColumnIds.has(column.id)
            ? getAdditionalColumnValue(row, column.id)
            : getCsvCellValue(row, column.id),
      }),
      {}
    )
  );

  downloadCsv({
    columns: csvColumns,
    data,
    filename,
  });
};

export const createDownloadDropdownItems = ({
  filenames = DEFAULT_DOWNLOAD_FILENAMES,
  additionalColumnMap,
  getAdditionalColumnValue,
} = {}) => [
  {
    title: "Download All Data",
    onClick: (tableInstance) =>
      downloadRowsAsCsv({
        tableInstance,
        rows: tableInstance.getPreFilteredRowModel().rows,
        filename: filenames.all,
        additionalColumnMap,
        getAdditionalColumnValue,
      }),
    icon: <IconSymbol icon="download_2" />,
  },
  {
    title: "Download Current Page",
    onClick: (tableInstance) =>
      downloadRowsAsCsv({
        tableInstance,
        rows: tableInstance.getPaginationRowModel().rows,
        filename: filenames.currentPage,
        additionalColumnMap,
        getAdditionalColumnValue,
      }),
    icon: <IconSymbol icon="pageview" />,
  },
  {
    title: "Download Filtered Data",
    onClick: (tableInstance) =>
      downloadRowsAsCsv({
        tableInstance,
        rows: tableInstance.getPrePaginationRowModel().rows,
        filename: filenames.filtered,
        additionalColumnMap,
        getAdditionalColumnValue,
      }),
    icon: <IconSymbol icon="filter_alt" />,
  },
];

export function MvdClearFiltersToolbar({ hasColumnFilters, onClearFilters }) {
  return (
    <div className="mvd-data-table__toolbar">
      <Button
        color="neutral"
        isDisabled={!hasColumnFilters}
        onClick={onClearFilters}
        size="sm"
        variant="outline"
      >
        Clear all filters
      </Button>
    </div>
  );
}

MvdClearFiltersToolbar.propTypes = {
  hasColumnFilters: PropTypes.bool.isRequired,
  onClearFilters: PropTypes.func.isRequired,
};
